import { Fragment } from 'react';

import { cn } from './cn';

/**
 * THE SKELETON VOCABULARY — the parts every console surface's pending state is
 * built from.
 *
 * WHY PARTS RATHER THAN ONE CONSOLE-WIDE SKELETON. A placeholder earns its place
 * only when it stands where the real content will stand. A table's placeholder
 * is `RowSkeleton` at the table's own column count, and it lives beside the
 * table primitives in `ui/dtable/` because a `<tr>` is only valid inside one;
 * everything else on this host — the fact panels, the two thread surfaces, the
 * forms, the category tree, the report bands — is composed from the blocks
 * below. `router/PendingRoute.tsx` is the FLOOR, and each surface carries a
 * `<Page>.Skeleton.tsx` drawn at its own geometry (`docs/v1/PRD-TDD.md`
 * §«Loading»: `RowSkeleton` rows at the real column count, or a matching block
 * skeleton for forms, threads and report bands).
 *
 * NOTHING HERE IS ANNOUNCED. Every part is `aria-hidden`: the surface owns one
 * polite `role="status"` announcement, and a screen reader walking forty empty
 * cells learns less than one that hears that single sentence.
 *
 * HEIGHTS ARE STATED, NEVER SCALED. `theme.spacing` in this repo REPLACES the
 * default scale with the design system's own eleven steps, so `h-40` and its
 * neighbours do not exist here — a block taller than `--space-10` states its
 * height as an explicit length rather than reaching for a Tailwind step that
 * silently resolves to nothing.
 */
export function Skeleton({ className }: { readonly className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'relative block overflow-hidden rounded-xs bg-skeleton-base',
        // The sweep travels toward the READING END — the left under `rtl`.
        'after:absolute after:inset-0 after:animate-skeleton-sheen after:bg-gradient-to-l',
        'after:from-transparent after:via-skeleton-sheen after:to-transparent',
        className,
      )}
    />
  );
}

/** A run of text lines. The last one is short, because a paragraph's last line
 *  is — a stack of equal bars reads as a table, not as prose. */
export function SkeletonLines({
  lines = 3,
  className,
}: {
  readonly lines?: number;
  readonly className?: string;
}) {
  return (
    <div aria-hidden="true" className={cn('grid gap-3', className)}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-4', index === lines - 1 ? 'w-1/2' : index % 3 === 1 ? 'w-5/6' : 'w-full')}
        />
      ))}
    </div>
  );
}

/**
 * The subject head — the `<h1>` and the lede beneath it.
 *
 * The state badge that sits beside the heading on the two detail surfaces is
 * NOT reserved: a badge placeholder occupies the one spot on those pages where a
 * wrong guess reads as a claim about the record. The heading block is drawn
 * narrower than full measure for the same reason a heading is: it is a name, not
 * a paragraph.
 */
export function SkeletonHead({ withLede = true }: { readonly withLede?: boolean }) {
  return (
    <div aria-hidden="true" className="mb-6 mt-3 grid gap-stack-tight">
      <Skeleton className="h-8 w-2/3 max-w-[24rem]" />
      {withLede ? <Skeleton className="h-4 w-1/2 max-w-[34rem]" /> : null}
    </div>
  );
}

/** A bordered panel at a stated height — the shape of every read-only card on
 *  this host before its facts arrive. */
export function SkeletonPanel({ className }: { readonly className?: string }) {
  return <Skeleton className={cn('w-full rounded-md', className)} />;
}

/**
 * A fact list inside its panel — `MetaGrid`'s own two-track geometry, label
 * column sized to content and value column taking the rest.
 *
 * `rows` is the panel's own row count. It is stated per surface rather than
 * defaulted generously: a facts card that draws six placeholder rows and
 * resolves to four collapses two rows of height on arrival, and the fact rows on
 * these surfaces are present-or-absent by design.
 */
export function SkeletonMetaGrid({ rows }: { readonly rows: number }) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-surface">
      <div className="p-4 md:p-5">
        <SkeletonMetaRows rows={rows} />
      </div>
    </div>
  );
}

/**
 * The same fact list WITHOUT the panel around it — `MetaGrid` is a bare `<dl>`,
 * and the two informational pages use it that way. A placeholder that drew the
 * border anyway would put a card on a surface that has none and then take it
 * away.
 */
export function SkeletonMetaRows({ rows }: { readonly rows: number }) {
  return (
    <div
      aria-hidden="true"
      className="m-0 grid gap-x-4 gap-y-2 [grid-template-columns:max-content_minmax(0,1fr)]"
    >
      {Array.from({ length: rows }, (_, index) => (
        // The label and its value are SIBLINGS in the two-track grid, exactly
        // as `<dt>` and `<dd>` are in the loaded panel: wrapping each pair in
        // a row element would take the value out of the value column and the
        // two tracks would stop aligning down the list.
        <Fragment key={index}>
          <Skeleton className="h-4 w-[6rem]" />
          <Skeleton className={cn('h-4', index % 3 === 1 ? 'w-2/3' : 'w-1/2')} />
        </Fragment>
      ))}
    </div>
  );
}

/** Label-and-control pairs at the console's real control height, which is what
 *  every authoring form collapses into. */
export function SkeletonFields({ rows = 4 }: { readonly rows?: number }) {
  return (
    <div aria-hidden="true" className="grid gap-5">
      {Array.from({ length: rows }, (_, index) => (
        <div key={index} className="grid gap-2">
          <Skeleton className="h-4 w-[7rem]" />
          <Skeleton className="h-[2.75rem] w-full rounded-sm" />
        </div>
      ))}
    </div>
  );
}

/**
 * A thread of messages, inside the ONE bordered box the loaded thread sits in.
 *
 * The container is the thread, not each message: the loaded surface draws a
 * single panel and separates the messages with a top border on every article
 * after the first, and the operator's own replies take the sunken surface
 * inside it. A column of separately-bordered cards reads as a list of records
 * rather than as a conversation, and it also draws a border the loaded thread
 * never has.
 *
 * Each row is the comment's own two-track shape — the `1.5rem` face and the
 * body beside it, with the author line above the message. The rows alternate,
 * because the inset surface is the only thing that says two parties are
 * talking.
 *
 * The composer below a thread is never reserved. Whether a case takes a reply
 * is decided by its served state.
 */
export function SkeletonThread({ messages = 3 }: { readonly messages?: number }) {
  return (
    <div
      aria-hidden="true"
      className="rounded-md border border-border bg-surface px-4 [&>div+div]:border-t [&>div+div]:border-border"
    >
      {Array.from({ length: messages }, (_, index) => (
        <div key={index} className={cn('flex gap-3 py-4', index % 2 === 1 && 'rounded-md bg-bg-sunken px-4')}>
          <Skeleton className="size-6 shrink-0 rounded-full" />

          <div className="min-w-0 flex-1">
            <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <Skeleton className="h-4 w-[7rem]" />
              <Skeleton className="h-3 w-[4rem]" />
            </div>
            <Skeleton className="h-4 w-full max-w-measure" />
            <Skeleton className="mt-2 h-4 w-2/3 max-w-measure" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * The category tree at its real depths.
 *
 * The indents are the tree's own: a root, a child inset by one step, a second
 * root. A flat run of bars would state that the taxonomy has no depth, and depth
 * is the one thing this surface exists to show — but nothing here draws a THIRD
 * level, because a childless root renders as a childless root and a placeholder
 * that promised a grandchild would be padding the tree with a fabricated node.
 */
export function SkeletonTree({ rows = 3 }: { readonly rows?: number }) {
  return (
    <div className="overflow-hidden rounded-md border border-border bg-surface">
      <div aria-hidden="true" className="grid gap-3 p-4">
        {Array.from({ length: rows }, (_, index) => (
          <Skeleton
            key={index}
            className={cn('h-5', index % 3 === 1 ? 'ms-4 w-1/2' : index % 3 === 2 ? 'w-2/5' : 'w-1/3')}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * A row of figure cards — the report bands' `StatGrid`, before the figures
 * arrive.
 *
 * The amount card spans the row at the base tier in the loaded grid, and the
 * placeholder rides the same `auto-fit` track so the cards step at the width the
 * real grid steps at rather than at a breakpoint restated here.
 */
export function SkeletonStatGrid({ cards = 3 }: { readonly cards?: number }) {
  return (
    <div aria-hidden="true" className="grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(11rem,1fr))]">
      {Array.from({ length: cards }, (_, index) => (
        <div key={index} className="grid gap-3 rounded-md border border-border bg-surface p-4">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-8 w-1/2" />
        </div>
      ))}
    </div>
  );
}
